import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Loader2, MapPin } from "lucide-react";

const validZip = (z) => /^\d{5}$/.test((z || "").trim());

function pinIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.45)"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
  });
}

const fmtLoc = (p, fallbackZip) =>
  p ? [[p.city, p.state].filter(Boolean).join(", "), p.zip].filter(Boolean).join(" ") : fallbackZip;

export default function RouteMap({ originZip, destZip, pickupDate, className = "" }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ready = validZip(originZip) && validZip(destZip);

  useEffect(() => {
    if (!ready) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/geo/route", { params: { origin: originZip, destination: destZip } })
      .then((r) => !cancelled && setData(r.data))
      .catch(() => !cancelled && setError("Route preview unavailable for these ZIP codes."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [originZip, destZip, ready]);

  useEffect(() => {
    if (!data || !mapEl.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(mapEl.current, { zoomControl: true, scrollWheelZoom: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
      }).addTo(mapRef.current);
    }
    const map = mapRef.current;
    if (layerRef.current) map.removeLayer(layerRef.current);
    const group = L.layerGroup().addTo(map);
    layerRef.current = group;

    const o = data.origin;
    const d = data.destination;
    const line = L.polyline(data.geometry, { color: "#1d6cb0", weight: 5, opacity: 0.9 }).addTo(group);
    L.marker([o.lat, o.lng], { icon: pinIcon("#16a34a") }).addTo(group).bindTooltip("Pickup");
    L.marker([d.lat, d.lng], { icon: pinIcon("#dc2626") }).addTo(group).bindTooltip("Destination");
    try {
      map.fitBounds(line.getBounds(), { padding: [28, 28] });
    } catch (_) {
      map.setView([o.lat, o.lng], 7);
    }
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [data]);

  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    },
    [],
  );

  if (!ready) {
    return (
      <div
        className={`flex items-center gap-2 rounded-lg border border-dashed border-border bg-card px-4 py-6 text-sm text-muted-foreground ${className}`}
        data-testid="route-map-empty"
      >
        <MapPin className="h-4 w-4" /> Enter pickup &amp; destination ZIP codes to preview the route.
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-lg border border-border bg-card ${className}`} data-testid="route-map">
      <div className="relative">
        <div ref={mapEl} style={{ height: 240, width: "100%" }} className="z-0" data-testid="route-map-canvas" />
        {loading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-card/70">
            <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
          </div>
        )}
        {data?.distance_miles != null && (
          <div
            className="absolute bottom-2 right-2 z-[600] rounded-full bg-zinc-900/85 px-3 py-1 text-xs font-bold text-white backdrop-blur"
            data-testid="route-distance"
          >
            {data.distance_miles} miles
          </div>
        )}
      </div>
      <div className="bg-zinc-900 p-4 text-white" data-testid="route-fromto">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">From</div>
          <div className="mt-0.5 text-sm font-semibold" data-testid="route-from">{fmtLoc(data?.origin, originZip)}</div>
          {pickupDate && <div className="mt-1 text-xs text-zinc-400" data-testid="route-pickup-date">{pickupDate}</div>}
        </div>
        <div className="my-3 border-t border-zinc-700" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">To</div>
          <div className="mt-0.5 text-sm font-semibold" data-testid="route-to">{fmtLoc(data?.destination, destZip)}</div>
        </div>
        {error && <div className="mt-2 text-xs text-[hsl(var(--warning))]">{error}</div>}
      </div>
    </div>
  );
}
